import { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
  Tag,
  TableProps,
  Button,
  Spin,
} from "antd";
import { HiOutlineArrowRightOnRectangle } from "react-icons/hi2";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import {
  GetMasterList,
  UpdateArchiveStatus,
} from "../../../utils/api.constant";
import EvalL2Form from "./form";
import {
  eResultCode,
  eWARRANTY_STATUS,
  eOUTPUT_STATUS,
  eOVERALLISTSTATUS,
  eEVAL_L1_WORKFLOW,
  ePrivileges,
  eARCHIVESTATUS,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../state/toast/slice";
import { TOverallList } from "../../../types/index";
import { UserContext } from "../../../context";
import {
  TContext,
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
} from "../../../types/config";
import {
  EditOutlined,
  FieldTimeOutlined,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import { useColumnSearch } from "../../../components/table/column-search";
import styles from "./overall.module.css";
import ActionCard from "../../../components/actionCard/actionCard";
import GenericPopover from "../../../components/pop-over";
import CustomDrawer from "../../../components/drawer";
import Level1Form from "../evaluation/l1-form";
import utils from "../../../utils";
import EvalLevel1View from "../evaluation/l1-view";
import CustomModal from "../../../components/modal";
import TimeLine from "../evaluation/time-line";
import Utils from "../../../utils";
const OVERALL_LIST_COLUMN_KEY = "overallList";

let tabs = [
  { label: "Not Scanned", value: eOVERALLISTSTATUS.NOT_SCANNED },
  {
    label: "Scanned-Waiting Eval",
    value: eOVERALLISTSTATUS.SCANNED_WAITING_EVAL,
  },
  { label: "Eval - L1", value: eOVERALLISTSTATUS.EVAL_L1 },
  { label: "Eval - L2", value: eOVERALLISTSTATUS.EVAL_L2 },
  { label: "No Fault", value: eOVERALLISTSTATUS.NO_FAULT },
  { label: "Tear Down", value: eOVERALLISTSTATUS.TEAR_DOWN },
  { label: "All", value: eOVERALLISTSTATUS.ALL },
];

export default function OverallList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TOverallList[]>([]);
  const context: TContext = useContext(UserContext);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const { getColumnSearchProps, getDateRangeFilterProps } =
    useColumnSearch<TOverallList>();
  const [view, setView] = useState<eOVERALLISTSTATUS>(
    eOVERALLISTSTATUS.NOT_SCANNED
  );
  const [renderTabs, setRenderTabs] = useState<TDropdownOptions[]>([]);
  const [showTimeLineModal, setShowTimeLineModal] = useState<boolean>(false);
  const [selectedRecId, setSelectedRecId] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  //STATE VARIABLE TO ENABLE THE ARCHIVE RECORD SELECTION
  const [enableSelection, setEnableSelection] = useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = useState<Array<number>>([]);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [archiveInProgress, setArchiveInProgress] = useState<boolean>(false);

  const getStatusColor = (status: eOVERALLISTSTATUS) => {
    switch (status) {
      case eOVERALLISTSTATUS.NOT_SCANNED:
        return "#B0BEC5";
      case eOVERALLISTSTATUS.SCANNED_WAITING_EVAL:
        return "#FFB74D";
      case eOVERALLISTSTATUS.EVAL_L1:
        return "#42A5F5";
      case eOVERALLISTSTATUS.EVAL_L2:
        return "#7E57C2";
      case eOVERALLISTSTATUS.NO_FAULT:
        return "#66BB6A";
      case eOVERALLISTSTATUS.TEAR_DOWN:
        return "#EF5350";
      case eOVERALLISTSTATUS.ALL:
        return "#78909C";
      default:
        return "#9E9E9E";
    }
  };

  const initialColumns: any[] = [
    {
      title: "Customer name",
      dataIndex: "customerName",
      key: "customerName",
      filters: utils.getUniqueFilters(data, "customerName"),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.customerName?.toLowerCase() === (value as string).toLowerCase(),
      sorter: (a: TOverallList, b: TOverallList) =>
        a.recordId?.localeCompare(b.recordId),
      filterSearch: true,
      // width: 180,
    },
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      ...getColumnSearchProps("recordId"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        Number(a.recordId) - Number(b.recordId),
      // width: 150,
      render: (value: number | string) => {
        return typeof value === "number"
          ? Math.floor(value)
          : value?.split(".")[0];
      },
    },
    {
      title: "PO Org",
      dataIndex: "poOrg",
      key: "poOrg",
      ...getColumnSearchProps("poOrg"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.poOrg?.localeCompare(b.poOrg),
      // width: 120,
    },
    {
      title: "FiRe ID #",
      dataIndex: "fireId",
      key: "fireId",
      ...getColumnSearchProps("fireId"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.fireId?.localeCompare(b.fireId),
      // width: 180,
    },
    {
      title: "Unit Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      ...getColumnSearchProps("serialNumber"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.serialNumber?.localeCompare(b.serialNumber),
      // width: 190,
    },
    {
      title: "Crate Serial No.",
      dataIndex: "assingedCrate",
      key: "assingedCrate",
      ...getColumnSearchProps("assingedCrate"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        (a.assingedCrate || "")?.localeCompare(b.assingedCrate || ""),
      // width: 165,
      render: (assingedCrate: string) => (assingedCrate ? assingedCrate : "-"),
    },
    {
      title: "Airtech model",
      dataIndex: "airtechModel",
      key: "airtechModel",
      filters: utils.getUniqueFilters(data, "airtechModel"),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.airtechModel?.toLowerCase() === (value as string).toLowerCase(),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.airtechModel?.localeCompare(b.airtechModel),
      // width: 190,
    },
    {
      title: "Type of Unit",
      dataIndex: "typeOfUnit",
      key: "typeOfUnit",
      filters: utils.getUniqueFilters(data, "typeOfUnit"),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.typeOfUnit?.toLowerCase() === (value as string).toLowerCase(),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        (a.typeOfUnit || "")?.localeCompare(b.typeOfUnit || ""),
      // width: 190,
      render: (typeOfUnit: string) => (typeOfUnit ? typeOfUnit : "-"),
    },
    {
      title: "Customer Part #",
      dataIndex: "productPartNumber",
      key: "productPartNumber",
      filters: utils.getUniqueFilters(data, "productPartNumber"),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.productPartNumber?.toLowerCase() ===
        (value as string).toLowerCase(),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a?.productPartNumber?.localeCompare(b?.productPartNumber),
      // width: 170,
    },
    {
      title: "Part #",
      dataIndex: "partNumber",
      key: "partNumber",
      filters: utils.getUniqueFilters(data, "partNumber"),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.partNumber?.toLowerCase() === (value as string).toLowerCase(),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.partNumber?.localeCompare(b.partNumber),
      // width: 110,
    },
    {
      title: "Received/Scanned Date",
      dataIndex: "scannedDate",
      key: "scannedDate",
      ...getDateRangeFilterProps("scannedDate"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.scannedDate).getTime() - new Date(b.scannedDate).getTime(),
      render: (scannedDate: string) =>
        scannedDate
          ? DateUtils.format(scannedDate, context?.user?.dateFormat, true)
          : "-",
      // width: 210,
    },
    {
      title: "Warranty date",
      dataIndex: "warrantyDateString",
      key: "warrantyDateString",
      ...getDateRangeFilterProps("warrantyDateString"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.warrantyDateString).getTime() -
        new Date(b.warrantyDateString).getTime(),
      // width: 160,
    },
    {
      title: "Warranty Status",
      dataIndex: "warrantyStatus",
      key: "warrantyStatus",
      // width: 240,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.warrantyStatus - b.warrantyStatus,
      filters: [
        // { text: "In Warranty", value: eWARRANTY_STATUS.IN_WARRANTY },
        { text: "Out of Warranty", value: eWARRANTY_STATUS.OUT_OF_WARRANTY },
        {
          text: "In Warranty & Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_CLAIMED,
        },
        {
          text: "In Warranty & Not Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED,
        },
      ],
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.warrantyStatus === value,
      render: (status: eWARRANTY_STATUS, record: TOverallList) => (
        <span
          style={{
            backgroundColor:
              record.outputStatus === eOUTPUT_STATUS.GREEN
                ? "var(--bs-success)"
                : record.outputStatus === eOUTPUT_STATUS.RED
                  ? "var(--bs-danger)"
                  : "",
            color: record.outputStatus ? "white" : "#333",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {status === eWARRANTY_STATUS.IN_WARRANTY
            ? "In Warranty"
            : status === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
              ? "In Warranty and Claimed"
              : status === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
                ? "In Warranty and Not Claimed"
                : status === eWARRANTY_STATUS.OUT_OF_WARRANTY
                  ? "Out of Warranty"
                  : "-"}
        </span>
      ),
    },
    {
      title: "Scanned by",
      dataIndex: "scannedByName",
      key: "scannedByName",
      ...getColumnSearchProps("scannedByName"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.scannedByName?.localeCompare(b.scannedByName),
      // width: 150,
    },
    {
      title: "Eval - L1 Date",
      dataIndex: "l1EvalDate",
      key: "l1EvalDate",
      ...getDateRangeFilterProps("l1EvalDate"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.l1EvalDate).getTime() - new Date(b.l1EvalDate).getTime(),
      render: (l1EvalDate: string) =>
        l1EvalDate
          ? DateUtils.format(l1EvalDate, context?.user?.dateFormat, true)
          : "-",
      // width: 150,
    },
    {
      title: "Failure mode",
      dataIndex: "failureMode",
      key: "failureMode",
      ...getColumnSearchProps("failureMode"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.failureMode?.localeCompare(b.failureMode),
      // width: 180,
    },
    {
      title: "Eval - L2 Date",
      dataIndex: "l2EvalDate",
      key: "l2EvalDate",
      ...getDateRangeFilterProps("l2EvalDate"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.l2EvalDate).getTime() - new Date(b.l2EvalDate).getTime(),
      render: (l2EvalDate: string) =>
        l2EvalDate
          ? DateUtils.format(l2EvalDate, context?.user?.dateFormat, true)
          : "-",
      // width: 150,
    },

    {
      title: "Disposition",
      dataIndex: "disposition",
      key: "disposition",
      ...getColumnSearchProps("disposition"),
      filterSearch: true,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.disposition?.localeCompare(b.disposition),
      // width: 150,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: (a: TOverallList, b: TOverallList) =>
        Number(a.price) - Number(b.price),
      // width: 150,
    },
    {
      title: "Manufactured date",
      dataIndex: "manufacturingDateString",
      key: "manufacturingDateString",
      ...getDateRangeFilterProps("manufacturingDateString"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.manufacturingDateString).getTime() -
        new Date(b.manufacturingDateString).getTime(),
      // width: 190,
    },
    {
      title: "Month",
      dataIndex: "manufacturingDate",
      key: "month",
      render: (manufacturingDate: string) =>
        manufacturingDate ? new Date(manufacturingDate).getMonth() + 1 : "-",
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.manufacturingDate).getMonth() -
        new Date(b.manufacturingDate).getMonth(),
      // width: 85,
    },
    {
      title: "Year",
      dataIndex: "manufacturingDate",
      key: "year",
      render: (manufacturingDate: string) =>
        manufacturingDate ? new Date(manufacturingDate).getFullYear() : "-",
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.manufacturingDate).getFullYear() -
        new Date(b.manufacturingDate).getFullYear(),
      // width: 70,
    },
    {
      title: "Status",
      dataIndex: "submissionStatus",
      key: "submissionStatus",
      render: (status: eEVAL_L1_WORKFLOW, record: TOverallList) =>
        status === eEVAL_L1_WORKFLOW.DRAFT ? (
          <Tag color="yellow">Draft</Tag>
        ) : status === eEVAL_L1_WORKFLOW.SUBMITTED ? (
          <Tag color="green">Submitted</Tag>
        ) : (
          "-"
        ),
      sorter: (a: TOverallList, b: TOverallList) =>
        a.submissionStatus - b.submissionStatus,
      filters: [
        { text: "Draft", value: eEVAL_L1_WORKFLOW.DRAFT },
        { text: "Submitted", value: eEVAL_L1_WORKFLOW.SUBMITTED },
      ],
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.submissionStatus === value,
      // width: 105,
    },
    {
      title: "Current Stage",
      dataIndex: "currentStage",
      key: "currentStage",
      // width: 210,
      sorter: (a: TOverallList, b: TOverallList) =>
        a.currentStage - b.currentStage,
      filters: tabs?.map((tab) => {
        return {
          text: tab.label,
          value: tab.value,
        };
      }),
      onFilter: (value: string | number | boolean, record: TOverallList) =>
        record.currentStage === value,
      render: (currentStage: eOVERALLISTSTATUS) => {
        const tab = tabs.find((t) => t.value === currentStage);
        const color = getStatusColor(currentStage);
        return <Tag color={color}>{tab ? tab.label : "-"}</Tag>;
      },
    },
    {
      title: "Part PO cost",
      dataIndex: "partPOCostNumber",
      key: "partPOCostNumber",
      sorter: (a: TOverallList, b: TOverallList) =>
        Number(a.partPOCostNumber) - Number(b.partPOCostNumber),
      // width: 150,
      render: (partPOCostNumber: number) => (
        <span>{partPOCostNumber ? `$ ${partPOCostNumber}` : "-"}</span>
      ),
    },
    {
      title: "Debrief Date",
      dataIndex: "debriefDateString",
      key: "debriefDateString",
      ...getDateRangeFilterProps("debriefDateString"),
      sorter: (a: any, b: any) =>
        new Date(a.debriefDateString).getTime() -
        new Date(b.debriefDateString).getTime(),
      // width: 145,
    },

    {
      title: "SA Subject",
      dataIndex: "saSubject",
      key: "saSubject",
      ...getColumnSearchProps("saSubject"),
      sorter: (a: TOverallList, b: TOverallList) =>
        a.saSubject?.localeCompare(b.saSubject),
      // width: 450,
    },
    {
      title: "Ship tracking #",
      dataIndex: "shipTrackingNumber",
      key: "shipTrackingNumber",
      ...getColumnSearchProps("shipTrackingNumber"),
      sorter: (a: TOverallList, b: TOverallList) =>
        a.shipTrackingNumber?.localeCompare(b.shipTrackingNumber),
      // width: 190,
    },
    {
      title: "Fedex Date",
      dataIndex: "fedexDate",
      key: "fedexDate",
      ...getDateRangeFilterProps("fedexDate"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.fedexDate).getTime() - new Date(b.fedexDate).getTime(),
      // width: 150,
    },
    {
      title: "Installation Date",
      dataIndex: "installationDate",
      key: "installationDate",
      ...getDateRangeFilterProps("installationDate"),
      sorter: (a: TOverallList, b: TOverallList) =>
        new Date(a.installationDate).getTime() -
        new Date(b.installationDate).getTime(),
      // width: 200,
    },
    {
      title: "Fedex Signed",
      dataIndex: "fedexSigned",
      key: "fedexSigned",
      ...getColumnSearchProps("fedexSigned"),
      sorter: (a: TOverallList, b: TOverallList) =>
        a.fedexSigned?.localeCompare(b.fedexSigned),
      // width: 150,
    },
    {
      title: "Present in Warranty file",
      dataIndex: "excelMessage",
      key: "excelMessage",
      ...getColumnSearchProps("excelMessage"),
      filterSearch: true,
      // width: 220,
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      // width: 80,
      render: (overall: TOverallList, record: TOverallList) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Eval - L1 form edit/view */}
                <>
                  {Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.VIEWSUMMARY
                  ) ? (
                    <CustomDrawer
                      placement="bottom"
                      trigger={
                        <ActionCard text="View Summary">
                          <FieldTimeOutlined />
                        </ActionCard>
                      }
                      component={EvalLevel1View}
                      componentProps={{
                        id: overall.id,
                        submissionStatus: overall.submissionStatus,
                        onRefreshList: () => {
                          fetchData();
                        },
                      }}
                      title="Eval - L1 View"
                    />
                  ) : null}

                  {view !== eOVERALLISTSTATUS.SCANNED_WAITING_EVAL &&
                  overall?.level1EvaluationId &&
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.PERFORMLEVEL1SCAN
                  ) ? (
                    <CustomDrawer
                      placement="bottom"
                      trigger={
                        <ActionCard text="Edit Eval - L1">
                          <EditOutlined />
                        </ActionCard>
                      }
                      component={Level1Form}
                      componentProps={{
                        id: overall.level1EvaluationId,
                        submissionStatus: overall.submissionStatus,
                        currentStage: overall.currentStage,
                        onRefreshList: () => {
                          fetchData();
                        },
                      }}
                      title="Eval - L1 Form"
                    />
                  ) : null}
                </>

                {/* Start Eval - L1 Form */}
                {overall.currentStage ===
                  eOVERALLISTSTATUS.SCANNED_WAITING_EVAL &&
                Utils.isUserHasAccess(
                  context.user?.privilegeList as TPrivilege[],
                  ePrivileges.PERFORMLEVEL1SCAN
                ) ? (
                  <CustomDrawer
                    placement="bottom"
                    trigger={
                      <ActionCard text="Start Eval-L1">
                        {/* <Bs1SquareFill /> */}
                        <HiOutlineArrowRightOnRectangle />
                      </ActionCard>
                    }
                    component={Level1Form}
                    componentProps={{
                      customerId: record?.customerId,
                      serialNumber: record?.serialNumber,
                      submissionStatus: overall.submissionStatus,
                      productId: record?.productId,
                      startEvalL1: true,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Eval - L1 Form"
                  />
                ) : null}

                {/* Start Eval - L2 Form */}
                {overall.currentStage === eOVERALLISTSTATUS.EVAL_L2 ||
                overall.currentStage === eOVERALLISTSTATUS.NO_FAULT ||
                (overall.currentStage === eOVERALLISTSTATUS.TEAR_DOWN &&
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.PERFORMLEVEL2SCAN
                  )) ? (
                  <CustomDrawer
                    placement="bottom"
                    trigger={
                      <ActionCard
                        text={
                          overall.level2EvaluationId
                            ? "Edit Eval - L2"
                            : "Start Eval - L2"
                        }
                      >
                        <HiOutlineArrowRightOnRectangle />
                      </ActionCard>
                    }
                    component={EvalL2Form}
                    componentProps={{
                      serialNumber: record.serialNumber,
                      level1EvalId: record.level1EvaluationId,
                      level2EvalId: record.level2EvaluationId || 0,
                      overallId: record.id,
                      onRefreshList: () => fetchData(),
                    }}
                    title="Eval - L2 Form"
                  />
                ) : null}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList as TPrivilege[],
                  ePrivileges.VIEWTIMELINE
                ) ? (
                  <ActionCard
                    text="Timeline"
                    onClick={() => timeLineModalAction(record.id)}
                  >
                    <FieldTimeOutlined />
                  </ActionCard>
                ) : null}

                {/* <ActionCard text="Delete" onClick={() => showDeleteModal(user)}>
                  <DeleteOutlined />
                </ActionCard> */}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        OVERALL_LIST_COLUMN_KEY
      );
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col])
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      } else {
        setColumns(initialColumns);
        setDraftColumns(initialColumns);
      }
    })();
    // setColumns(initialColumns);
  }, [view, context?.user]);

  //Dyanmaically pushed the tabs as per privileges
  useEffect(() => {
    const privilegeList = context?.user?.privilegeList as TPrivilege[];

    let tabsToRender: { label: string; value: eOVERALLISTSTATUS }[] = [];

    const privilegeChecks = [
      {
        privilege: ePrivileges.VIEWNOTSCANNED,
        tab: { label: "Not Scanned", value: eOVERALLISTSTATUS.NOT_SCANNED },
      },
      {
        privilege: ePrivileges.VIEWWAITINGEVAL,
        tab: {
          label: "Scanned-Waiting Eval",
          value: eOVERALLISTSTATUS.SCANNED_WAITING_EVAL,
        },
      },
      {
        privilege: ePrivileges.VIEWEVALL1LIST,
        tab: { label: "Eval - L1", value: eOVERALLISTSTATUS.EVAL_L1 },
      },
      {
        privilege: ePrivileges.VIEWEVALL2LIST,
        tab: { label: "Eval - L2", value: eOVERALLISTSTATUS.EVAL_L2 },
      },
      {
        privilege: ePrivileges.VIEWNOFAULTLIST,
        tab: { label: "No Fault", value: eOVERALLISTSTATUS.NO_FAULT },
      },
      {
        privilege: ePrivileges.VIEWTEARDOWNLIST,
        tab: { label: "Tear Down", value: eOVERALLISTSTATUS.TEAR_DOWN },
      },
    ];

    // Push all tabs the user has access to
    privilegeChecks.forEach(({ privilege, tab }) => {
      if (utils.isUserHasAccess(privilegeList, privilege)) {
        tabsToRender.push(tab);
      }
    });

    // Only if ALL privileges are present → add "All" tab
    const hasAllPrivileges = privilegeChecks.every(({ privilege }) =>
      utils.isUserHasAccess(privilegeList, privilege)
    );

    if (hasAllPrivileges) {
      tabsToRender.push({
        label: "All",
        value: eOVERALLISTSTATUS.ALL,
      });
    }

    setRenderTabs(tabsToRender);
  }, [context?.user]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          recordViewId: view,
        },
      };

      const response: TResponseModel = await post(
        GetMasterList,
        requestPayload
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setData(data);
        // updateColumnFilters(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // PROCEED TO ARCHIVE RECORDS
  const proceedToArchive = async (records: Array<number>) => {
    try {
      setArchiveInProgress(true);
      const requestPayload = {
        data: {
          archiveStatus: eARCHIVESTATUS.ARCHIVED,
          customerUploadList: records.map((id) => ({ id })),
        },
      };

      const response: TResponseModel = await post(
        UpdateArchiveStatus,
        requestPayload
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setShowArchiveModal(false);
        setSelectedRecords([]);
        await fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setArchiveInProgress(false);
    }
  };

  useEffect(() => {
    fetchData();
    // setColumns(initialColumns);
  }, [view]);

  const updateColumnFilters = (dataset: TOverallList[]) => {
    const getFilters = (key: keyof TOverallList) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-"
            ) as string[]
        )
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };

    const customerNameFilters = getFilters("customerName");
    const partNumberFilters = getFilters("partNumber");
    const productPartNumberFilters = getFilters("productPartNumber");
    const airtechModelFilters = getFilters("airtechModel");
    const typeOfUnitFilters = getFilters("typeOfUnit");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        switch (col.key) {
          case "customerName":
            return { ...col, filters: customerNameFilters };
          case "partNumber":
            return { ...col, filters: partNumberFilters };
          case "productPartNumber":
            return { ...col, filters: productPartNumberFilters };
          case "airtechModel":
            return { ...col, filters: airtechModelFilters };
          case "typeOfUnit":
            return { ...col, filters: typeOfUnitFilters };
          default:
            return col;
        }
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(data);
  }, [data]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const getFilteredData = () => {
    let result = [...data];

    Object.entries(activeFilters).forEach(([key, filterValue]) => {
      if (filterValue && filterValue.length > 0) {
        const column = columns.find(
          (col) => col.key === key || col.dataIndex === key
        );

        if (column && column.onFilter) {
          result = result.filter((item) => {
            return filterValue.some((filter: any) =>
              column.onFilter!(filter, item)
            );
          });
        } else {
          result = result.filter((item) => {
            const itemValue = item[key as keyof TOverallList];
            return filterValue.includes(itemValue);
          });
        }
      }
    });

    if (globalsearchText) {
      result = result.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(globalsearchText.toLowerCase())
        )
      );
    }

    return result;
  };

  const filteredData = getFilteredData();

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(OVERALL_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(OVERALL_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns?.filter(
    (col) =>
      !col?.hidden &&
      (view === eOVERALLISTSTATUS.SCANNED_WAITING_EVAL ||
        view === eOVERALLISTSTATUS.EVAL_L1 ||
        view === eOVERALLISTSTATUS.EVAL_L2 ||
        view === eOVERALLISTSTATUS.NO_FAULT ||
        view === eOVERALLISTSTATUS.TEAR_DOWN ||
        (col?.key as string) !== "actions")
  );

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item)))
  ).filter((key) => key !== "excelStatus" && key !== "outputStatus");

  const exportData = data.map((item) => {
    const { excelStatus, outputStatus, ...restItems } = item;
    const mapped = {
      ...restItems,
      warrantyStatus:
        item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY
          ? "In Warranty"
          : item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
            ? "In Warranty and Claimed"
            : item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
              ? "In Warranty and Not Claimed"
              : item.warrantyStatus === eWARRANTY_STATUS.OUT_OF_WARRANTY
                ? "Out of Warranty"
                : "-",
      submissionStatus:
        item.submissionStatus === eEVAL_L1_WORKFLOW.DRAFT
          ? "Draft"
          : item.submissionStatus === eEVAL_L1_WORKFLOW.SUBMITTED
            ? "Submitted"
            : "-",
      currentStage:
        tabs.find((tab) => tab.value === item.currentStage)?.label || "-",
    };

    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const exportFilteredData = filteredData.map((item) => {
    const { excelStatus, outputStatus, ...restItems } = item;
    const mapped = {
      ...restItems,
      warrantyStatus:
        item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY
          ? "In Warranty"
          : item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
            ? "In Warranty and Claimed"
            : item.warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
              ? "In Warranty and Not Claimed"
              : item.warrantyStatus === eWARRANTY_STATUS.OUT_OF_WARRANTY
                ? "Out of Warranty"
                : "-",
      submissionStatus:
        item.submissionStatus === eEVAL_L1_WORKFLOW.DRAFT
          ? "Draft"
          : item.submissionStatus === eEVAL_L1_WORKFLOW.SUBMITTED
            ? "Submitted"
            : "-",
      currentStage:
        tabs.find((tab) => tab.value === item.currentStage)?.label || "-",
    };

    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setGlobalSearchText("");

    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const timeLineModalAction = (id: number) => {
    setSelectedRecId(id);
    setShowTimeLineModal(true);
  };

  // rowSelection object indicates the need for row selection
  const rowSelection: TableProps<TOverallList>["rowSelection"] = {
    selectedRowKeys: selectedRecords,
    fixed: true,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
    onChange: (selectedRowKeys: React.Key[], selectedRows: TOverallList[]) => {
      setSelectedRecords(selectedRows.map((r: TOverallList) => r.id));
    },
  };

  return (
    <>
      <div className={styles.parentContainer}>
        {renderTabs.map((tab) => (
          <div
            key={tab.value}
            className={`${styles.tab} ${
              view === tab.value ? styles.activeTab : styles.inactiveTab
            }`}
            onClick={() => setView(tab.value as number)}
          >
            <span
              className={
                view === tab.value ? styles.activeText : styles.inactiveText
              }
            >
              {tab.label}
            </span>
          </div>
        ))}
      </div>
      <div className="pageBody">
        <PageHeader
          icon={<IdcardFilled />}
          title={`${data?.length} Total Records ${enableSelection ? `| ${selectedRecords.length} Records Selected` : ""}`}
          size="large"
        >
          <Space className="headerItems" wrap>
            {selectedRecords?.length ? (
              <Button
                type="primary"
                danger
                ghost
                onClick={() => setShowArchiveModal(true)}
              >
                Mark as Archived
              </Button>
            ) : null}
            {utils.isUserHasAccess(
              context?.user?.privilegeList as TPrivilege[],
              ePrivileges.PERFORMARCHIVEACTION
            ) ? (
              <Button
                type="primary"
                onClick={() => {
                  setEnableSelection((prev) => !prev);
                  setSelectedRecords([]);
                }}
              >
                {enableSelection ? "Disable Archive" : "Enable Archive"}
              </Button>
            ) : null}

            <Input
              placeholder="Filter by keywords"
              value={globalsearchText}
              onChange={(e) => setGlobalSearchText(e.target.value)}
              style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
              prefix={<SearchOutlined />}
            />
            <ExportToCSV
              data={exportData}
              filteredData={exportFilteredData}
              allColumns={columns}
              visibleColumns={visibleColumns}
              moduleName="Overall List"
            />
            <Tooltip title="Reset Filters">
              <FilterOutlined onClick={handleResetAll} />
            </Tooltip>
            <Tooltip title="Configure Columns">
              <OrderedListOutlined
                onClick={() => {
                  setDraftColumns(columns);
                  setDrawerVisible(true);
                }}
              />
            </Tooltip>
          </Space>
        </PageHeader>

        <div className="tableWrap">
          <Table
            rowSelection={enableSelection ? { ...rowSelection } : undefined}
            columns={visibleColumns}
            dataSource={filteredData}
            loading={isLoading}
            pagination={{
              position: ["bottomLeft"],
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} total entries`,
            }}
            onChange={handleTableChange}
            bordered={false}
            size="small"
            // sticky
            scroll={{
              x: "max-content",
            }}
            rowKey="id"
          />
        </div>

        <Drawer
          title="Customize Columns"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          style={{ transition: "transform 0.3s ease-in-out" }}
        >
          <ColumnManager
            columns={draftColumns}
            initialColumns={initialColumns}
            onDragEnd={handleDragEnd}
            onToggleColumn={toggleColumn}
            onResetColumns={handleResetColumns}
            onSave={handleSaveColumns}
            onCancel={() => setDrawerVisible(false)}
          />
        </Drawer>

        <CustomModal
          title="Timeline"
          isOpen={showTimeLineModal}
          onOk={() => {}}
          onCancel={() => {
            setShowTimeLineModal(false);
            setSelectedRecId(null);
          }}
          hideOkButton={true}
          okText=""
          cancelText="Close"
          cusHeader={false}
          width={1080}
        >
          <TimeLine id={selectedRecId || 0} showHeader={true}></TimeLine>
        </CustomModal>
        <CustomModal
          title="Confirm Action"
          isOpen={showArchiveModal}
          onOk={() => proceedToArchive(selectedRecords)}
          onCancel={() => {
            setShowArchiveModal(false);
          }}
          hideOkButton={archiveInProgress ? true : false}
          hideCancelButton={archiveInProgress ? true : false}
          okText="Proceed"
          cancelText="Close"
          cusHeader={false}
        >
          {archiveInProgress ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spin size="large" />
              <div style={{ marginTop: 12 }}>
                Archiving {selectedRecords.length} records, please wait…
              </div>
            </div>
          ) : (
            <>
              You are about to archive {selectedRecords.length} records. Do you
              want to continue?
            </>
          )}
        </CustomModal>
      </div>
    </>
  );
}
